import { useEffect, useState } from "react";

import { parseDetail } from "../utils/errors";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  TextField,
  Typography,
} from "@mui/material";
import GroupsIcon from "@mui/icons-material/Groups";
import { useTenant } from "../hooks/useTenant";
import useRequests from "../hooks/http";

export default function Organisations() {
  const { t } = useTranslation();
  const { get, post } = useRequests();
  const { activeTenant, loading: tenantLoading } = useTenant();
  const navigate = useNavigate();
  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newOrg, setNewOrg] = useState({ name: "", slug: "" });
  const [error, setError] = useState("");

  useEffect(() => {
    if (!activeTenant) {
      setOrganisations([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    get(`/admin/tenants/${activeTenant.id}/organisations`)
      .then(({ data, status }) => {
        if (status === 200 && Array.isArray(data)) {
          setOrganisations(data);
        } else if (status !== 200) {
          setError(parseDetail(data?.detail, "Failed to load organisations"));
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [activeTenant, get]);

  const handleCreate = async () => {
    if (!activeTenant) return;
    setError("");
    const { data, status } = await post(`/admin/tenants/${activeTenant.id}/organisations`, null, newOrg);
    if (status === 201 || status === 200) {
      setOrganisations((prev) => [...prev, data]);
      setCreateOpen(false);
      setNewOrg({ name: "", slug: "" });
    } else {
      setError(parseDetail(data?.detail, "Failed to create organisation"));
    }
  };

  if (tenantLoading || loading) return <LinearProgress />;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography variant="h4">{t("organisations.title")}</Typography>
        {activeTenant && (
          <Button variant="contained" onClick={() => setCreateOpen(true)}>
            {t("organisations.newButton")}
          </Button>
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t("organisations.description")}
      </Typography>

      {error && !createOpen && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {organisations.length === 0 && !error && (
        <Typography color="text.secondary">No organisations found.</Typography>
      )}

      <Grid container spacing={2}>
        {organisations.map((org) => (
          <Grid item xs={12} sm={6} md={4} key={org.id}>
            <Card variant="outlined">
              <CardActionArea onClick={() => navigate(`/app/organisations/${org.id}`)}>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <GroupsIcon color="primary" />
                    <Typography variant="h6">{org.name}</Typography>
                    <Box
                      component="span"
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: org.active ? "success.main" : "text.disabled",
                        flexShrink: 0,
                        ml: "auto",
                      }}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {org.slug}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Organisation</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t("organisations.description")}
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            label="Name"
            fullWidth
            margin="normal"
            value={newOrg.name}
            onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value })}
          />
          <TextField
            label="Slug"
            fullWidth
            margin="normal"
            placeholder="e.g. my-organisation"
            helperText={t("organisations.slugHelp")}
            value={newOrg.slug}
            onChange={(e) => setNewOrg({ ...newOrg, slug: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!newOrg.name || !newOrg.slug}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
